<template>
    <div style="display: contents">
        <slot :open="openDialog">
            <el-button v-bind="$attrs" @click="openDialog">
                Edit facility
            </el-button>
        </slot>

        <el-dialog v-model="dialogOpened" append-to-body @opened="onDialogOpened">
            <template #title>
                <h4>Edit facility</h4>
            </template>

            <h5>General information</h5>
            <el-form :model="facility" label-position="top" ref="facilityForm" inline @validate="onFacilityFormValidated" :rules="facilityFormRules">
                <el-form-item prop="owner" style="flex-basis: 100%" :disabled="!facility?.meta?.fields?.owner?.edit" label="Facility PI">
                    <SelectUser v-model="facility.owner" :multiple="false" search-roles />
                </el-form-item>

                <el-form-item prop="type" :disabled="!facility?.meta?.fields?.type?.edit" label="Facility type" class="flex-grow-1 flex-shrink-1" style="flex-basis: 0">
                    <el-select placeholder="Facility type" v-model="facility.type" value-key="id">
                        <el-option
                            v-for="status in facilityTypes"
                            :key="status.id"
                            :value="status"
                            :label="status.name"
                        />
                    </el-select>
                </el-form-item>

                <el-form-item prop="displayed_status" :disabled="!facility?.meta?.fields?.displayed_status?.edit" label="Facility displayed status" class="flex-grow-1 flex-shrink-1" style="flex-basis: 0">
                    <el-select placeholder="Facility displayed status" v-model="facility.displayed_status" value-key="id">
                        <el-option
                            v-for="status in facilityStatuses"
                            :key="status.id"
                            :value="status"
                            :label="status.name"
                        />
                    </el-select>
                </el-form-item>
            </el-form>

            <template v-if="canEditReviewers">
                <h5>
                    Facility reviewers
                </h5>

                <el-form inline ref="reviewersForm" :model="initialAcceptance.reviewers" label-position="top" :rules="reviewersFormRules" @validate="onReviewersFormValidated">
                    <el-form-item prop="lidar" label="Lidar reviewer" class="flex-grow-1">
                        <SelectUser v-model="initialAcceptance.reviewers.lidar" :with-role="lidar_reviewer_role" />
                    </el-form-item>
                    <el-form-item prop="lidar_data" label="Lidar data reviewer" class="flex-grow-1">
                        <SelectUser v-model="initialAcceptance.reviewers.lidar_data" :with-role="lidar_data_reviewer_role" />
                    </el-form-item>
                    <div style="flex-basis: 100%"></div><!-- Just to break line -->
                    <el-form-item prop="photometer" label="Photometer reviewer" class="flex-grow-1">
                        <SelectUser v-model="initialAcceptance.reviewers.photometer" :with-role="photometer_reviewer_role" />
                    </el-form-item>
                    <el-form-item prop="photometer_data" label="Photometer data reviewer" class="flex-grow-1">
                        <SelectUser v-model="initialAcceptance.reviewers.photometer_data" :with-role="photometer_data_reviewer_role" />
                    </el-form-item>
                </el-form>
            </template>

            <template #footer>
                <!-- <el-tooltip content="All facility information fields are required in order to update the facility" :disabled="facilityFormValid" :enterable="false">
                    <ConfirmButton button-type="primary" @confirm="saveFacility()" :disabled="!facilityFormValid" confirmationText="Please confirm you want to save the changes made to this national facility">Save facility information</ConfirmButton>
                </el-tooltip>
                <el-tooltip v-if="canEditReviewers" content="All reviewer fields need to be filled in in order to update the list" :disabled="reviewersFormValid" :enterable="false">
                    <ConfirmButton button-type="primary" @confirm="saveReviewers()" :disabled="!reviewersFormValid" confirmationText="Please confirm you want to save selected acceptance reviewers">Set acceptance reviewers</ConfirmButton>
                </el-tooltip>
                <el-button type="danger" icon="Close" plain @click="closeDialog()">Close</el-button> -->
                <ConfirmButton button-type="primary" @confirm="saveFacility()" confirmationText="Please confirm you want to save the changes made to this national facility">Save facility information</ConfirmButton>
                <ConfirmButton button-type="primary" @confirm="saveReviewers()" confirmationText="Please confirm you want to save selected acceptance reviewers">Save reviewers</ConfirmButton>
                <el-button type="danger" icon="Close" plain @click="closeDialog()">Close</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import SelectUser from '@/components/user/SelectUser.vue'
import ConfirmButton from '@/components/button/ConfirmButton.vue'

import api from '@/lib/api'

import {mapActions, mapGetters} from 'vuex'

export default {
    name: 'EditFacility',

    components: { SelectUser, ConfirmButton },

    emits: ['update:modelValue', 'saved', 'cancel'],

    props: {
        modelValue: { type: Object, required: true }
    },

    computed: {
        ...mapGetters('types', ['facilityTypes', 'facilityStatuses']),
        ...mapGetters('user', ['fullRolesList']),

        facility: {
            get () { return this.modelValue },
            set(value) { this.$emit('update:modelValue', value) }
        },

        initialAcceptance: {
            get () { return this.facility?.initial_acceptance },
            set (value) { this.facility.initial_acceptance = { ...this.facility.initial_acceptance, ...value } }
        },

        allowedToEditReviewers () {
            return this.initialAcceptance?.meta?.fields?.reviewers?.edit
        },

        canEditReviewers () { return this.allowedToEditReviewers },

        hasAnyReviewer () { return this.initialAcceptance?.reviewers ? Object.values(this.initialAcceptance?.reviewers).some(reviewer => !!reviewer.id) : false },

        lidar_reviewer_role () { return this.fullRolesList.find(role => role.name === "Lidar Reviewer") },
        lidar_data_reviewer_role () { return this.fullRolesList.find(role => role.name === "Lidar Data Reviewer") },
        photometer_reviewer_role () { return this.fullRolesList.find(role => role.name === "Photometer Reviewer") },
        photometer_data_reviewer_role () { return this.fullRolesList.find(role => role.name === "Photometer Data Reviewer") },
    },

    data: () => ({
        dialogOpened: false,
        loading: false,

        originalFacility: {},
        originalReviewers: {},

        facilityFormValid: false,
        reviewersFormValid: false,

        facilityFormRules: {
            owner: { required: true, message: 'Must select facility PI' },
            type: { required: true, message: 'Must select facility type' },
            displayed_status: { required: true, message: 'Must select displayed status' },
        },

        reviewersFormRules: {
            lidar: { required: true, message: 'Must select lidar reviewer' },
            lidar_data: { required: true, message: 'Must select lidar data reviewer' },
            photometer: { required: true, message: 'Must select photometer reviewer' },
            photometer_data: { required: true, message: 'Must select photometer data reviewer' }
        },
    }),

    methods: {
        ...mapActions('types', ['getFacilityTypes', 'getFacilityStatuses']),
        ...mapActions('user', ['getFullRolesList']),

        openDialog () {
            // Store initial data
            this.originalFacility = {...this.facility}
            this.originalReviewers = {...this.initialAcceptance?.reviewers}

            // Open dialog
            this.dialogOpened = true
        },

        onDialogOpened () {
            if (this.$refs.reviewersForm) {
                // Reviewers form might not be displayed
                // this.$refs.reviewersForm.validate().catch(() => {this.$refs.reviewersForm.clearValidate()});

                this.$refs.reviewersForm.fields.forEach (field => {
                    field.validate().catch (() => { field.clearValidate() })
                })
            }

            // this.$refs.facilityForm.validate().catch(() => {this.$refs.facilityForm.clearValidate()})

            this.$refs.facilityForm.fields.forEach (field => {
                    field.validate().catch (() => { field.clearValidate() })
                })
        },

        onFacilityFormValidated () {
            this.facilityFormValid = this.$refs.facilityForm.fields.filter (field => field.validateState !== 'success').length === 0
        },

        onReviewersFormValidated () {
            this.reviewersFormValid = this.$refs.reviewersForm.fields.filter (field => field.validateState !== 'success').length === 0
        },

        saveFacility () {
            this.loading = true

            api.facilities.editFacility (this.facility.id, {
                owner: this.facility.owner,
                type: this.facility.type,
                displayed_status: this.facility.displayed_status
            })
            .then (response => {
                this.facility = {...response.data, initial_acceptance: this.initialAcceptance}
                this.originalFacility = {...this.facility}
                this.$emit('saved', this.facility)

                this.originalFacility = response.data
                this.$emit('saved', this.originalFacility)

                this.$notify({
                    title: 'Success',
                    message: `Facility information updated.`,
                    type: 'success'
                })
            })
            .catch(error => {
                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Could not update facility information: ${err}`,
                    type: 'error'
                })
            })
            .finally ( () => { this.loading = false } )
        },

        saveReviewers () {
            this.loading = true
            api.facilities.patchInitialAcceptance (this.facility.id, {
                reviewers: this.initialAcceptance.reviewers
            })
            .then (response => {
                this.initialAcceptance = response.data

                this.originalFacility = {...this.facility, initial_acceptance: response.data}
                this.originalFacility.initial_acceptance = response.data
                
                this.facility.initial_acceptance = response.data
                this.originalFacility = {...this.facility}

                this.originalReviewers = this.initialAcceptance.reviewers
                this.$emit('saved', this.facility)

                this.$notify({
                    title: 'Success',
                    message: `Facility acceptance reviewers were set.`,
                    type: 'success'
                })
            })
            .catch(error => {
                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Could set facility acceptance reviewers: ${err}`,
                    type: 'error'
                })
            })
            .finally ( () => { this.loading = false } )
        },

        closeDialog () {
            this.resetFacility()
            this.dialogOpened = false
        },

        resetFacility () {
            this.facility = this.originalFacility
            this.initialAcceptance.reviewers = this.originalReviewers
            this.$emit('cancel', this.originalFacility)
        }
    },

    created () {
        this.getFullRolesList()
        this.getFacilityTypes()
        this.getFacilityStatuses()
    },
}
</script>